"use client";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import { StatsCard } from "../../../../../components/stats-card";

import FilterButtons from "../../../../../components/universal-filter-tabs";
import CollectionTable from "../components/CollectionTable";
import CollectionModal from "../components/CollectionModal";
import Pagination from "../../../../../components/Pagination";
import FormInput from "../../../../../components/FormInput";
import styles from "../css/collectionDetails.module.css";
import tableStyles from "../css/collectionTable.module.css";

import { fetchPendingChronicCards } from "../../../../../api/dispensary/script/chronicApi";
import { fetchPendingPackagedCards } from "../../../../../api/dispensary/script/packagedScriptApi";
import { fetchScriptDetails } from "../../../../../api/dispensary/script/processScriptApi";
import { useAuth } from "../../../../../context/AuthContext.jsx";

export function CollectionDetails() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState("Awaiting");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const itemsPerPage = 6;

  const filterButtons = ["Awaiting", "Collected", "Overdue"];

  // Reset pagination when date filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate]);

  const [collections, setCollections] = useState([]);

  const formatYMDToDMY = (s) => {
    if (!s || typeof s !== "string") return "";
    const [y, m, d] = s.split("-");
    if (!y || !m || !d) return "";
    return `${d}/${m}/${y}`;
  };

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const branchcode = user?.branchcode || undefined;
        const [chronic, packaged] = await Promise.all([
          fetchPendingChronicCards({ branchcode }),
          fetchPendingPackagedCards({ branchcode }),
        ]);

        const raw = [...(chronic || []), ...(packaged || [])].filter(
          (it) => (it.deliveryOption || "Collect") === "Collect"
        );

        const mapped = await Promise.all(
          raw.map(async (it) => {
            let itemsLabel = "";
            let amountLabel = "";
            try {
              const details = await fetchScriptDetails(it.prescriptionId);
              const drugs = Array.isArray(details?.drugs) ? details.drugs : [];
              if (drugs.length) {
                itemsLabel = drugs
                  .map((d) => d.drugName || "")
                  .filter(Boolean)
                  .join(", ");
                const gross = drugs.reduce((sum, d) => {
                  const cp =
                    typeof d.costPrice === "number"
                      ? d.costPrice
                      : parseFloat(String(d.costPrice || "").replace(/^[^0-9.-]*/, ""));
                  const qty = parseFloat(d.quantity ?? d.qty ?? 0) || 0;
                  if (!Number.isFinite(cp) || cp <= 0 || qty <= 0) return sum;
                  return sum + cp * qty;
                }, 0);
                if (gross > 0) {
                  amountLabel = `R${gross.toFixed(2)}`;
                }
              }
            } catch (e) {}

            const preferredDate = it.preferredDate || "";
            const displayDate = formatYMDToDMY(preferredDate);
            const baseStatus = it.status === "Overdue" ? "overdue" : "awaiting";

            return {
              id: it.scriptNo || String(it.prescriptionId),
              patient: it.name || "",
              items: itemsLabel,
              scheduledTime: it.preferredTime || "",
              paymentType: it.paymentType || "",
              date: displayDate,
              amount: amountLabel,
              status: baseStatus,
              collectedDate: null,
              paymentId: it.paymentId,
              repeatId: it.repeatId,
              prescriptionId: it.prescriptionId,
            };
          })
        );

        if (!alive) return;
        setCollections(mapped);
      } catch (e) {
        if (!alive) return;
        console.error("Failed to load collections", e);
        setCollections([]);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [user?.branchcode]);

  const awaitingColumns = [
    { header: "Patient", accessor: "patient" },
    { header: "Items", accessor: "items" },
    { header: "Scheduled Time", accessor: "scheduledTime" },
    { header: "Payment Type", accessor: "paymentType" },
    {
      header: "Details",
      accessor: "details",
      render: (value, row) => (
        <button className={tableStyles.viewButton} onClick={() => handleViewDetails(row.id)}>
          View
        </button>
      ),
    },
  ];

  const collectedColumns = [
    { header: "Patient", accessor: "patient" },
    { header: "Items", accessor: "items" },
    { header: "Pickup Date", accessor: "date" },
    { header: "Payment Type", accessor: "paymentType" },
    {
      header: "Details",
      accessor: "details",
      render: (value, row) => (
        <button className={tableStyles.viewButton} onClick={() => handleViewDetails(row.id)}>
          View
        </button>
      ),
    },
  ];

  const overdueColumns = collectedColumns;

  // Scheduled Collections = Awaiting + Overdue (per requirement)
  const scheduledCount = collections.filter((c) => c.status === "awaiting" || c.status === "overdue").length;
  const collectedCount = collections.filter((c) => c.status === "collected").length;
  const overdueCount = collections.filter((c) => c.status === "overdue").length;

  // Helpers for date parsing and normalization (prefer yyyy/MM/dd)
  const parseDateStr = (s) => {
    if (!s || typeof s !== 'string') return null;
    const norm = s.trim().replace(/[.\-\s]+/g, '/');
    // Try yyyy/MM/dd first
    let m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(norm);
    if (m) {
      const yyyy = Number(m[1]);
      const mm = Number(m[2]);
      const dd = Number(m[3]);
      const d = new Date(yyyy, mm - 1, dd);
      return isNaN(d.getTime()) ? null : d;
    }
    // Fallback dd/MM/yyyy
    m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(norm);
    if (m) {
      const dd = Number(m[1]);
      const mm = Number(m[2]);
      const yyyy = Number(m[3]);
      const d = new Date(yyyy, mm - 1, dd);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const normalize = (d) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null);

  // Filter data based on selected tab and optional date range
  const statusFiltered = collections.filter((c) => {
    const statusOk =
      (activeFilter === "Awaiting" && c.status === "awaiting") ||
      (activeFilter === "Collected" && c.status === "collected") ||
      (activeFilter === "Overdue" && c.status === "overdue");
    return statusOk;
  });

  const shouldApplyDateFilter =
    (activeFilter === "Collected" || activeFilter === "Overdue") && (fromDate || toDate);

  const dateFiltered = shouldApplyDateFilter
    ? statusFiltered.filter((row) => {
        const from = normalize(fromDate);
        const to = normalize(toDate);
        const sd = normalize(parseDateStr(row.date));
        const cd = normalize(parseDateStr(row.collectedDate));

        const inRange = (date) => {
          if (!date) return false;
          if (from && date < from) return false;
          if (to && date > to) return false;
          return true;
        };

        if (activeFilter === "Collected") {
          return inRange(cd) || inRange(sd);
        }
        // Overdue: filter by date
        return inRange(sd);
      })
    : statusFiltered;

  // Pagination derived data (based on filtered results)
  const totalPages = Math.max(1, Math.ceil(dateFiltered.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = dateFiltered.slice(startIndex, startIndex + itemsPerPage);

  const handleViewDetails = (id) => {
    const collection = collections.find((item) => item.id === id);
    setSelectedCollection(collection);
    setIsModalOpen(true);
  };

  const handleComplete = (id) => {
    setCollections((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, status: "collected", collectedDate: new Date().toLocaleString() } : c
      )
    );
    setIsModalOpen(false);
    setSelectedCollection(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCollection(null);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSetFilter = (val) => {
    setActiveFilter(val);
    setCurrentPage(1);
  };

  return (
    <div className={styles.pageWrapper}>
      {/* Navbar positioned at top */}
      <Navbar />
      
      {/* Main content area that respects the sidebar flex layout */}
      <div className={styles.mainContent}>
        {/* Stats Cards */}
        <div className={styles.metricsGrid}>
          <StatsCard
            title="Scheduled Collections"
            value={scheduledCount}
            icon="boxes"
            inlineIcon
            interactive={false}
          />
          <StatsCard
            title="Collected"
            value={collectedCount}
            icon="calendar-check-2"
            inlineIcon
            interactive={false}
          />
          <StatsCard
            title="Overdue"
            value={overdueCount}
            icon="hourglass"
            inlineIcon
            interactive={false}
          />
        </div>
        
        {/* Filter Section */}
        <div className={styles.filterSection}>
          <FilterButtons
            buttons={filterButtons}
            activeButton={activeFilter}
            onButtonClick={handleSetFilter}
          />
        </div>

        {(activeFilter === "Collected" || activeFilter === "Overdue") && (
          <div className={styles.dateFiltersCard}>
            <div className={styles.dateFiltersRow}>
              <div className={styles.inlineFilter}>
                <span>From</span>
                <FormInput
                  label=""
                  type="date"
                  name="fromDate"
                  value={fromDate ? fromDate.toISOString().slice(0, 10) : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFromDate(v ? new Date(v) : null);
                  }}
                />
              </div>
              <div className={styles.inlineFilter}>
                <span>To</span>
                <FormInput
                  label=""
                  type="date"
                  name="toDate"
                  value={toDate ? toDate.toISOString().slice(0, 10) : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setToDate(v ? new Date(v) : null);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className={styles.tableSection}>
          <CollectionTable
            columns={
              activeFilter === "Awaiting"
                ? awaitingColumns
                : activeFilter === "Collected"
                ? collectedColumns
                : overdueColumns
            }
            data={paginatedData}
          />
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        </div>

        {/* Modal */}
        <CollectionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          collectionData={selectedCollection}
          activeFilter={activeFilter}
          onComplete={() => selectedCollection && handleComplete(selectedCollection.id)}
        />
      </div>
      
      {/* Footer positioned at bottom */}
      <Footer />
    </div>
  );
}

export default CollectionDetails;